import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { CoreRow } from '../components/AssetProperties'
import { assetTypeAllowsMultiple } from '../lib/asset-type-inheritance'
import { parentAssetPatchBody } from '../lib/asset-parent'
import { getLocalizedName } from '../lib/localization'
import { useAsset, useAssetTypes, useUpdateAsset } from './useQueries'

/**
 * The asset's "core" columns — type, external code, lifecycle dates — surfaced as
 * editable rows at the top of the Identité section.
 *
 * These are asset *columns*, not property values, so they are saved by PATCHing
 * the asset rather than through the properties API. They used to be edited from
 * the page header; the header now only *shows* them (type chip, code chip) and
 * every asset-detail tab that renders a property registry hosts the editing:
 * the Data tab for the types that have one, the Properties tab for the rest
 * (equipment, components…). Both call this hook so the two stay in step.
 */
export function useAssetCoreRows(org: string, assetUid: string, assetTypeUid?: string) {
    const { t: tAssets, i18n } = useTranslation('assets')
    const { data: asset } = useAsset(org, assetUid)
    const { data: assetTypes = [] } = useAssetTypes(org)
    // Resolves the parent's name so the row shows one instead of a uid; the
    // hook no-ops on the empty string until the asset itself has loaded.
    const { data: parentAsset } = useAsset(org, (asset?.parent_asset_uid as string) || '')
    const updateAsset = useUpdateAsset(org, assetUid)

    const allowsMultiple = assetTypeAllowsMultiple(
        assetTypes,
        (asset?.asset_type_uid as string) || assetTypeUid,
    )

    const assetTypeLabel = (uid?: string): string | null => {
        const type = uid ? assetTypes.find((at) => at.uid === uid) : undefined
        if (!type) return null

        return getLocalizedName(type.name as unknown as Record<string, string>, i18n.language?.split('-')[0] || 'en') || null
    }

    const coreRows = useMemo<CoreRow[]>(() => {
        if (!asset) return []
        const externalIds = (asset.external_ids as Record<string, unknown> | undefined) || {}
        const code = externalIds.code
        return [
            {
                code: 'asset_type_uid',
                category: 'identity',
                label: tAssets('detail.asset_type'),
                dataType: 'asset_type',
                value: (asset.asset_type_uid as string) || assetTypeUid || null,
                // What the read-only row prints (a resident, a viewer): the
                // type's name rather than its uid.
                valueLabel: assetTypeLabel((asset.asset_type_uid as string) || assetTypeUid),
            },
            {
                code: 'external_code',
                category: 'identity',
                label: tAssets('detail.external_code'),
                dataType: 'text',
                value: code != null ? String(code) : null,
            },
            // Parent and quantity used to be edited from the page header — the
            // parent as the muted ancestry line under the title (which read as a
            // second breadcrumb, since the real one says the same thing), the
            // quantity as its own box above the tab strip. Both are asset
            // columns like the four around them, so they belong in the registry
            // with the rest rather than in chrome of their own.
            {
                code: 'parent_asset_uid',
                category: 'identity',
                label: tAssets('detail.parent_asset'),
                dataType: 'asset',
                value: (asset.parent_asset_uid as string) || null,
                valueLabel: (parentAsset?.name as string) || (parentAsset?.code as string) || null,
            },
            // Editable only for types whose ontology allows several of the
            // thing (the `multiple` flag, inherited) — the API refuses
            // `quantity > 1` elsewhere. For every other type the row is still
            // shown, read-only, with the reason: a silently missing field
            // reads as a bug ("where do I set the number?"), a locked one
            // explains that this type is tracked one asset per unit.
            {
                code: 'quantity',
                category: 'identity',
                label: tAssets('detail.quantity'),
                dataType: 'number' as const,
                value: asset.quantity != null ? String(asset.quantity) : null,
                ...(allowsMultiple
                    ? {}
                    : {
                        readOnly: true,
                        hint: tAssets('detail.quantity_unit_tracked'),
                    }),
            },
            {
                code: 'start_date',
                category: 'identity',
                label: tAssets('detail.effective'),
                dataType: 'date',
                value: (asset.start_date as string) || null,
            },
            {
                code: 'end_date',
                category: 'identity',
                label: tAssets('detail.expiration'),
                dataType: 'date',
                value: (asset.end_date as string) || null,
            },
        ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [asset, assetTypeUid, allowsMultiple, parentAsset, tAssets, assetTypes, i18n.language])

    const onSaveCoreRow = useCallback(async (code: string, value: string | null) => {
        let body: Record<string, unknown>
        if (code === 'external_code') {
            const externalIds = (asset?.external_ids as Record<string, unknown> | undefined) || {}
            body = { external_ids: { ...externalIds, code: value ?? '' } }
        } else if (code === 'asset_type_uid') {
            if (!value) return // the type is not nullable — an empty pick is a no-op
            body = { asset_type_uid: value }
        } else if (code === 'start_date' || code === 'end_date') {
            body = { [code]: value || null }
        } else if (code === 'parent_asset_uid') {
            body = parentAssetPatchBody(value)
        } else if (code === 'quantity') {
            const parsed = Number(value)
            if (!value || !Number.isFinite(parsed) || parsed < 1) {
                throw new Error(tAssets('detail.quantity_invalid', {
                    defaultValue: 'Quantity must be a whole number of at least 1',
                }))
            }
            body = { quantity: Math.round(parsed) }
        } else {
            return
        }
        await updateAsset.mutateAsync(body as never)
    }, [asset, updateAsset, tAssets])

    return { coreRows, onSaveCoreRow }
}
